import { createStore, useStore } from 'zustand';
import type { NotificationDraft, Severity } from './describe';

export interface Notification extends NotificationDraft {
  id: number;
  /** When it happened (ms since the epoch, local clock). */
  at: number;
}

export interface NotificationState {
  /** The log, newest first. */
  items: Notification[];
  /** Toasts still on screen, oldest first. */
  toasts: Notification[];
  unread: number;
  /** Whether new notifications also pop up as toasts (a user setting; the log always records them). */
  toastsEnabled: boolean;
  add(draft: NotificationDraft): void;
  dismiss(id: number): void;
  markRead(): void;
  clear(): void;
  setToastsEnabled(on: boolean): void;
  /** Puts back a log saved earlier (after a reload); nothing pops up again. */
  restore(items: Notification[], unread: number): void;
}

export const MAX_LOG = 200;
export const MAX_TOASTS = 4;
/** Toasts of these severities stay until dismissed, because the user must not miss them. */
export const STICKY: Severity[] = ['danger'];

export function createNotificationStore(now: () => number = Date.now) {
  let nextId = 1;
  return createStore<NotificationState>()((set) => ({
    items: [],
    toasts: [],
    unread: 0,
    toastsEnabled: true,
    add(draft) {
      const n: Notification = { ...draft, id: nextId++, at: now() };
      set((s) => ({
        items: [n, ...s.items].slice(0, MAX_LOG),
        unread: s.unread + 1,
        toasts: s.toastsEnabled ? [...s.toasts, n].slice(-MAX_TOASTS) : s.toasts,
      }));
    },
    dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    markRead: () => set({ unread: 0 }),
    clear: () => set({ items: [], toasts: [], unread: 0 }),
    setToastsEnabled: (toastsEnabled) =>
      set((s) => ({ toastsEnabled, toasts: toastsEnabled ? s.toasts : [] })),
    restore(items, unread) {
      nextId = Math.max(nextId, ...items.map((i) => i.id + 1));
      set({ items: items.slice(0, MAX_LOG), unread: Math.min(unread, items.length), toasts: [] });
    },
  }));
}

export const notifications = createNotificationStore();

export const useNotifications = <T>(selector: (s: NotificationState) => T): T =>
  useStore(notifications, selector);

const SEVERITIES: Severity[] = ['info', 'success', 'warning', 'danger'];

/** A saved log, repaired: anything that is not a notification is dropped. */
export function readSavedLog(raw: string | null): { items: Notification[]; unread: number } {
  try {
    const parsed = JSON.parse(raw ?? '') as { items?: unknown; unread?: unknown };
    const items = (Array.isArray(parsed.items) ? parsed.items : []).filter(
      (n): n is Notification =>
        !!n &&
        typeof n === 'object' &&
        typeof (n as Notification).id === 'number' &&
        typeof (n as Notification).at === 'number' &&
        typeof (n as Notification).title === 'string' &&
        typeof (n as Notification).text === 'string' &&
        SEVERITIES.includes((n as Notification).severity),
    );
    return { items, unread: typeof parsed.unread === 'number' ? parsed.unread : 0 };
  } catch {
    return { items: [], unread: 0 };
  }
}

/**
 * Keeps the log across reloads of this tab, for this user only: it lives in the tab's session storage (gone when the tab
 * closes, never shared with another person who signs in on the same browser). Returns the function that stops it.
 */
export function persistNotifications(
  store: typeof notifications,
  userId: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof sessionStorage === 'undefined'
    ? null
    : sessionStorage,
): () => void {
  if (!storage) return () => undefined;
  const key = `adiox.notifications.${userId}`;
  try {
    const saved = readSavedLog(storage.getItem(key));
    if (saved.items.length > 0 && store.getState().items.length === 0) {
      store.getState().restore(saved.items, saved.unread);
    }
  } catch {
    // storage blocked: the log simply starts empty
  }
  return store.subscribe((state, previous) => {
    if (state.items === previous.items && state.unread === previous.unread) return;
    try {
      storage.setItem(key, JSON.stringify({ items: state.items, unread: state.unread }));
    } catch {
      // full or blocked: keep going without saving
    }
  });
}

// ---------------------------------------------------------------- sounds and desktop notifications
export type SoundSetting = 'off' | 'alerts' | 'all';

/** Whether a notification of this severity plays a sound: "alerts" means warnings and dangers (margin call, stop out, alerts). */
export const playsSound = (setting: SoundSetting, severity: Severity) =>
  setting === 'all' || (setting === 'alerts' && (severity === 'warning' || severity === 'danger'));

/** Desktop notifications are for what must not be missed while the tab is in the background: warnings, dangers and fills. */
export const wantsDesktop = (enabled: boolean, severity: Severity, pageHidden: boolean) =>
  enabled && pageHidden && severity !== 'info';
